/**
 * Right-click actions on the map.
 *
 * One reusable menu; each screen supplies the actions. Command offers GOTO,
 * orbit and ROI against the selected aircraft; Planning offers waypoint and
 * pattern insertion into the draft. The menu itself knows nothing about either
 * — it takes a builder that turns a clicked point into a list of items, and
 * returns null to mean "nothing to offer here" (no selection, no permission),
 * in which case no menu opens.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useMapController, type MapContextPoint } from '@/map/MapCanvas';

export interface MenuItem {
  label: string;
  /** Secondary line, e.g. the coordinate the action will use. */
  sub?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Render a divider instead of an action. `label` is ignored. */
  separator?: boolean;
  onSelect?: () => void;
}

export type MenuBuilder = (point: MapContextPoint) => MenuItem[] | null;

interface OpenMenu {
  x: number;
  y: number;
  items: MenuItem[];
}

const MENU_WIDTH = 216;

export function MapContextMenu({ build }: { build: MenuBuilder }): ReactNode {
  const controller = useMapController();
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  // Hold the latest builder so the subscription, which is set up once, always
  // calls the current closure rather than a stale one captured at mount.
  const buildRef = useRef(build);
  buildRef.current = build;

  useEffect(
    () =>
      controller.onContextMenu((point) => {
        const items = buildRef.current(point);
        if (!items || items.length === 0) {
          setMenu(null);
          return;
        }
        setMenu({ x: point.x, y: point.y, items });
      }),
    [controller],
  );

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    // Any left-click anywhere, or a pan/zoom, dismisses it.
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    const release = controller.onClick(close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
      release();
    };
  }, [menu, controller]);

  if (!menu) return null;

  // Keep the menu on-screen: flip left / clamp up when near an edge.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
  // On a 375px-tall landscape phone a full menu is taller than the screen, so
  // it is clamped to the top edge and scrolls rather than running off it.
  const width = Math.min(MENU_WIDTH, vw - 16);
  const left = Math.max(8, Math.min(menu.x, vw - width - 12));
  const top = Math.max(8, Math.min(menu.y, vh - menu.items.length * 40 - 40));

  return (
    <div
      className="glass scroll-thin pointer-events-auto absolute z-50 overflow-y-auto rounded-[14px] py-1.5"
      style={{ left, top, width, maxHeight: vh - top - 8 }}
      // Stop the window pointerdown listener from closing before the click lands.
      onPointerDown={(event) => event.stopPropagation()}
    >
      {menu.items.map((item, index) =>
        item.separator ? (
          <div key={index} className="my-1 h-px bg-white/10" />
        ) : (
          <button
            key={index}
            type="button"
            disabled={item.disabled}
            onClick={() => {
              setMenu(null);
              item.onSelect?.();
            }}
            className="flex w-full flex-col items-start justify-center gap-0.5 px-3.5 py-2 text-left transition-colors pointer-coarse:min-h-[44px] hover:bg-white/10 disabled:pointer-events-none disabled:opacity-40"
          >
            <span
              className="font-mono text-[11.5px] font-semibold tracking-wide"
              style={{ color: item.danger ? 'oklch(0.72 0.19 28)' : '#f4f4f5' }}
            >
              {item.label}
            </span>
            {item.sub ? (
              <span className="font-mono text-[9.5px] text-white/45">{item.sub}</span>
            ) : null}
          </button>
        ),
      )}
    </div>
  );
}

export default MapContextMenu;
